import type { SessionUser } from '@motoveo/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';

export function DashboardPage({ user }: { user: SessionUser }) {
  const health = useQuery({ queryKey: ['health'], queryFn: api.health, refetchInterval: 15_000 });
  const recent = useQuery({ queryKey: ['submissions', ''], queryFn: () => api.submissions.list({ limit: 5 }) });

  return (
    <Layout user={user}>
      <h2>System health</h2>
      {health.isLoading ? (
        <p className="muted">Checking…</p>
      ) : health.isError ? (
        <p className="error">Health check failed: {(health.error as Error).message}</p>
      ) : health.data ? (
        <div className="card">
          <p>
            Status: <span className={`badge badge-${health.data.status}`}>{health.data.status}</span>
          </p>
          <ul>
            {health.data.checks.map((c) => (
              <li key={c.name}>
                {c.name}: <strong>{c.ok ? 'ok' : 'down'}</strong>
                {typeof c.latency_ms === 'number' ? ` (${c.latency_ms}ms)` : ''}
                {c.detail ? ` — ${c.detail}` : ''}
              </li>
            ))}
          </ul>
          <p className="muted">
            version {health.data.version} · uptime {health.data.uptime_s}s
          </p>
        </div>
      ) : null}

      <div className="row-between">
        <h2>Recent submissions</h2>
        <Link to="/submissions" className="nav-link">
          View all →
        </Link>
      </div>
      {recent.data && recent.data.items.length > 0 ? (
        <div className="card">
          <ul className="plain">
            {recent.data.items.map((s) => (
              <li key={s.id}>
                <Link to={`/submissions/${s.id}`}>{s.public_ref}</Link> <span className="pill">{s.status}</span>{' '}
                <span className="muted">{s.vehicle_model ?? s.raw_vin ?? '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="muted">
          No submissions yet. <Link to="/intake">Start one →</Link>
        </p>
      )}
    </Layout>
  );
}
